import React from 'react';
import { IconProvider } from '@/components/ui';
import { CategorySelectionProps } from './FormWizardTypes';
import { LoadingSpinner } from '@/components/loaders/loading-spinner';

/**
 * CategorySelection - Displays available categories as clickable cards
 * Used as the first step in the form wizard
 */
export function CategorySelection({
  categories,
  onSelectCategory,
  isLoading = false,
}: CategorySelectionProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" text="Loading categories..." />
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <IconProvider iconstring="fa-exclamation-triangle" className="h-16 w-16 text-yellow-500 mb-4" />
        <h3 className="text-xl font-semibold mb-2">No Categories Configured</h3>
        <p className="text-muted-foreground mb-4 max-w-md">
          Please configure category mappings in Settings → Database Export before creating objects.
        </p>
        <p className="text-sm text-muted-foreground">
          Category mappings link object types (like "Quest") to their templates.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">What would you like to create?</h1>
        <p className="text-lg text-muted-foreground">Choose a category to get started</p>
      </div>

      {/* Category Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <button
            key={category.name}
            onClick={() => onSelectCategory(category)}
            className="group relative p-6 bg-card border-2 border-border rounded-lg hover:border-brand-brand_purple hover:shadow-lg transition-all duration-200 text-left"
          >
            {/* Icon */}
            <div className="mb-4 p-3 bg-brand-brand_purple/10 rounded-lg inline-block group-hover:bg-brand-brand_purple/20 transition-colors">
              <IconProvider 
                iconstring={category.icon || 'fa-folder'} 
                className="h-8 w-8 text-brand-brand_purple" 
              />
            </div>

            {/* Category Name */}
            <h3 className="text-xl font-semibold mb-2 group-hover:text-brand-brand_purple transition-colors">
              {category.name}
            </h3>

            {/* Template Info */}
            <p className="text-sm text-muted-foreground mb-2">
              Template: {category.templateName}
            </p>

            {/* Description */}
            {category.description && (
              <p className="text-sm text-muted-foreground">
                {category.description}
              </p>
            )}

            {/* Arrow Icon */}
            <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
              <IconProvider 
                iconstring="fa-arrow-right" 
                className="h-5 w-5 text-brand-brand_purple" 
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
